import { useEffect, useState } from "react";
import qs from "qs";
import apiService from "../lib/apiServices";

type UseDataQueryParams = Record<string, any>;

interface UseDataResult {
  data: any | null;
  isLoading: boolean;
  error: string | null;
}

export const useEntityData = (
  endpoint: string,
  queryParams?: UseDataQueryParams,
  shouldFetch: boolean = true
): UseDataResult => {
  const [data, setData] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const base_url = process.env.NEXT_PUBLIC_DOMAIN_URL;

  useEffect(() => {
    if (!shouldFetch || data) return; // Prevent fetching data if shouldFetch is false

    const fetchData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Serialize queryParams and merge with the default 'publicationState'
        const paramsWithPublicationState = {
          ...queryParams,
          publicationState: "live",
        };

        // Create a unique cache key based on the endpoint and serialized query params
        const queryString = qs.stringify(paramsWithPublicationState, {
          arrayFormat: "repeat",
          encodeValuesOnly: true,
        });

        const cacheKey = `${base_url}/${endpoint}?${queryString}`;

        const cache = await caches.open(`${endpoint}-cache`);
        const cachedResponse = await cache.match(cacheKey);

        if (cachedResponse) {
          const cachedData = await cachedResponse.json();
          setData(cachedData);
        } else {
          // Pass the merged paramsWithPublicationState to apiService.get
          const response = await apiService.get(
            `/${endpoint}`,
            paramsWithPublicationState
          );

          const responseClone = new Response(JSON.stringify(response));
          cache.put(cacheKey, responseClone);

          setData(response);
        }
      } catch (err: any) {
        setError(err.message || "Failed to fetch data.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [endpoint, JSON.stringify(queryParams), shouldFetch]);

  return { data, isLoading, error };
};
